import React from 'react';
import { Badge } from '../../ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '../../ui/tooltip';
import {
  Clock,
  CheckCircle,
  AlertTriangle,
  HelpCircle
} from 'lucide-react';
import {
  getFreshnessColor,
  getFreshnessCategory,
  formatRelativeTime,
  formatAbsoluteTime,
  getDataFreshnessWarning
} from '../../../utils/inventoryHelpers';

/**
 * InventoryFreshnessIndicator Component
 * Displays data freshness status with visual indicators and tooltips
 *
 * @param {Object} props
 * @param {string|Date} props.timestamp - The timestamp to display
 * @param {boolean} props.showIcon - Whether to show the icon (default: true)
 * @param {boolean} props.showLabel - Whether to show the text label (default: true)
 * @param {boolean} props.showTooltip - Whether to show detailed tooltip (default: true)
 * @param {'inline'|'chip'|'full'} props.variant - Display variant (default: 'inline')
 * @param {'small'|'medium'} props.size - Component size (default: 'small')
 */
function InventoryFreshnessIndicator({
  timestamp,
  showIcon = true,
  showLabel = true,
  showTooltip = true,
  variant = 'inline',
  size = 'small'
}) {
  if (!timestamp) {
    return (
      <div className="flex items-center gap-1">
        <HelpCircle className={`text-muted-foreground ${size === 'small' ? 'h-4 w-4' : 'h-5 w-5'}`} />
        <span className="text-xs text-muted-foreground">未知</span>
      </div>
    );
  }

  const freshness = getFreshnessCategory(timestamp);
  const relativeTime = formatRelativeTime(timestamp);
  const absoluteTime = formatAbsoluteTime(timestamp);
  const warning = getDataFreshnessWarning(freshness);
  const color = getFreshnessColor(timestamp);

  // Get icon based on freshness
  const getIcon = () => {
    const iconClass = size === 'small' ? 'h-4 w-4' : 'h-5 w-5';
    switch (freshness) {
      case 'fresh':
        return <CheckCircle className={`${iconClass} text-success`} />;
      case 'recent':
        return <Clock className={`${iconClass} text-warning`} />;
      case 'stale':
        return <AlertTriangle className={`${iconClass} text-destructive`} />;
      default:
        return <HelpCircle className={`${iconClass} text-muted-foreground`} />;
    }
  };

  // Tooltip content
  const tooltipContent = (
    <div className="space-y-1">
      <p className="font-medium text-sm">{absoluteTime}</p>
      <p className="text-xs">更新于: {relativeTime}</p>
      {warning && (
        <p className="text-xs text-warning mt-1">⚠️ {warning}</p>
      )}
    </div>
  );

  // Render based on variant
  switch (variant) {
    case 'chip':
      const chipContent = (
        <Badge
          variant="outline"
          className={`cursor-${showTooltip ? 'help' : 'default'}`}
          style={{
            backgroundColor: `${color}15`,
            color: color,
            borderColor: `${color}30`
          }}
        >
          {showIcon && <span className="mr-1">{getIcon()}</span>}
          {showLabel && relativeTime}
        </Badge>
      );

      return showTooltip ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>{chipContent}</TooltipTrigger>
            <TooltipContent>{tooltipContent}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : chipContent;

    case 'full':
      const fullContent = (
        <div className="flex flex-col gap-1 p-2 rounded-lg border border-gray-200 bg-muted">
          <div className="flex items-center gap-2">
            {showIcon && getIcon()}
            <span className="text-sm font-medium">数据更新时间</span>
          </div>
          <span className="text-xs text-muted-foreground">
            {relativeTime} ({absoluteTime})
          </span>
          {warning && (
            <span className="text-xs text-warning flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              {warning}
            </span>
          )}
        </div>
      );

      return showTooltip ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>{fullContent}</TooltipTrigger>
            <TooltipContent>点击查看详细信息</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : fullContent;

    case 'inline':
    default:
      const inlineContent = (
        <div
          className={`flex items-center gap-1 ${showTooltip ? 'cursor-help' : ''}`}
        >
          {showIcon && getIcon()}
          {showLabel && (
            <span
              className={`text-xs ${freshness === 'stale' ? 'font-bold underline' : ''}`}
              style={{ color }}
            >
              {relativeTime}
            </span>
          )}
          {freshness === 'stale' && (
            <AlertTriangle className="h-3 w-3 text-warning ml-0.5" />
          )}
        </div>
      );

      return showTooltip ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>{inlineContent}</TooltipTrigger>
            <TooltipContent>{tooltipContent}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : inlineContent;
  }
}

export default InventoryFreshnessIndicator;
